import { useState } from 'react';
import { trpc } from '../../../utils/trpc/trpc.ts';
import { PeriodOptionSet } from '../period-selection/createPeriodOptions.ts';
import { OperatorRulesDialog } from './operatorRulesDialog.tsx';
import { ReportOperatorControl } from './reportOperatorControl.tsx';
import { ReportTimeControls } from '../period-selection/reportTimeControls.tsx';
import { ReportSettings } from './reportSettings.ts';
import { ReportShiftControl } from './reportShiftControl.tsx';
import { ReportTargetControl } from './reportTargetControl.tsx';
import { ReportTypeControl } from './reportTypeControl.tsx';

interface Props {
    style?: React.CSSProperties;
    reportSettings: ReportSettings;
    setReportSettings: (reportSettings: ReportSettings) => void;
    periodOptionSet: PeriodOptionSet;
    onShowReportTypeInfo: () => void;
}

export function ReportControls({
    style,
    reportSettings,
    setReportSettings,
    periodOptionSet,
    onShowReportTypeInfo,
}: Props) {
    const [isOperatorsInfoOpen, setIsOperatorsInfoOpen] = useState(false);
    const { data: isAdmin } = trpc.user.isAdmin.useQuery(undefined, {
        refetchOnWindowFocus: false,
    });

    const shiftsDisplayEnabled = [
        'Availability', 'Measurement delays', 'Scraps', 'Measurement counts',
    ].includes(reportSettings.reportType);
    const shiftFiltersEnabled = [
        'Availability', 'Measurement delays', 'Shift delays', 'Scraps', 'Measurement counts',
    ].includes(reportSettings.reportType);

    const operatorCountsEnabled = reportSettings.resolution === 'Days'
        && reportSettings.reportType !== 'Shift delays'
        && reportSettings.reportType !== 'Setups';
    const reportTargetEnabled = reportSettings.reportType === 'Availability';
    const targetValueEnabled = reportTargetEnabled
        && reportSettings.mainValue === 'Percentage'
        && isAdmin === true;

    return (
        <div className='report-controls' style={style}>
            <ReportTypeControl
                reportSettings={reportSettings}
                setReportSettings={setReportSettings}
                periodOptionSet={periodOptionSet}
                onShowReportTypeInfo={onShowReportTypeInfo}
            />
            <ReportTimeControls
                reportSettings={reportSettings}
                setReportSettings={setReportSettings}
                periodOptionSet={periodOptionSet}
            />
            {reportTargetEnabled && (
                <ReportTargetControl
                    reportSettings={reportSettings}
                    setReportSettings={setReportSettings}
                    targetValueEnabled={targetValueEnabled}
                />
            )}
            {shiftFiltersEnabled && (
                <ReportShiftControl
                    reportSettings={reportSettings}
                    setReportSettings={setReportSettings}
                    shiftsDisplayEnabled={shiftsDisplayEnabled}
                />
            )}
            {operatorCountsEnabled && (
                <ReportOperatorControl
                    reportSettings={reportSettings}
                    setReportSettings={setReportSettings}
                    onShowOperatorsInfo={() => setIsOperatorsInfoOpen(true)}
                />
            )}
            {isOperatorsInfoOpen && (
                <OperatorRulesDialog onClose={() => setIsOperatorsInfoOpen(false)} />
            )}
        </div>
    );
}
